import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../services/authApi';
import { tokens } from '../../theme/tokens';

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Where the browser goes once the account is gone: the login page, with a notice. */
export const AFTER_DELETION_URL = '/login?deleted=1';

/**
 * Deleting the account: what goes and what stays in groups, said before the
 * button, and a typed word so it is never one tap by accident. Afterwards the
 * session is cleared and the page reloads, so nothing of the account stays in
 * memory either.
 */
const DeleteAccountDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation('settings');
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [failed, setFailed] = useState(false);
  const word = t('deleteAccount.word');
  const items = t('deleteAccount.items', { returnObjects: true }) as unknown as string[];
  const confirmed = typed.trim().toUpperCase() === word;

  const close = () => {
    if (deleting) return;
    setTyped('');
    setFailed(false);
    onClose();
  };

  const remove = async () => {
    setDeleting(true);
    setFailed(false);
    try {
      await authApi.deleteAccount();
      localStorage.removeItem('financy_token');
      localStorage.removeItem('financy_user');
      window.location.assign(AFTER_DELETION_URL);
    } catch (error) {
      setFailed(true);
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth='sm' aria-labelledby='delete-account-title'>
      <DialogTitle id='delete-account-title' sx={{ fontFamily: tokens.display, fontWeight: 700 }}>
        {t('deleteAccount.dialogTitle')}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box component='p' sx={{ m: 0, fontWeight: 600 }}>{t('deleteAccount.intro')}</Box>
        <Box component='ul' sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1, color: tokens.ink2, lineHeight: 1.5 }}>
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </Box>
        <Box component={RouterLink} to='/privacidade' target='_blank' sx={{ alignSelf: 'flex-start', color: tokens.accent, fontSize: 14 }}>
          {t('deleteAccount.privacyLink')}
        </Box>
        <TextField
          label={t('deleteAccount.confirmLabel', { word })}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          autoComplete='off'
          disabled={deleting}
        />
        {failed && <Alert severity='error'>{t('deleteAccount.error')}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} disabled={deleting} sx={{ color: tokens.ink2 }}>
          {t('deleteAccount.cancel')}
        </Button>
        <Button
          variant='contained'
          onClick={remove}
          disabled={!confirmed || deleting}
          startIcon={deleting ? <CircularProgress size={18} color='inherit' /> : undefined}
          sx={{ bgcolor: tokens.danger, '&:hover': { bgcolor: '#912018' } }}
        >
          {t('deleteAccount.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAccountDialog;
